import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  X, Trash2, Loader2, CalendarClock, Stethoscope, Save, Pencil, AlertTriangle, ExternalLink,
} from 'lucide-react';
import {
  MEDICAL_EVENT_TYPES, MEDICAL_EVENT_LABELS,
  updateAnimalMedicalEvent, deleteAnimalMedicalEvent,
} from '@rehome/shared';
import type { AnimalMedicalReminder, MedicalEventType } from '@rehome/shared';

interface ReminderDialogProps {
  reminder: AnimalMedicalReminder | null;
  shelterId: string | undefined;
  onClose: () => void;
  /** Sikeres módosítás vagy törlés után — a lista/naptár frissítéséhez. */
  onChanged: () => void;
}

const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none';
const labelCls = 'block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1';

const daysUntil = (dateStr: string | null): number | null => {
  if (!dateStr) return null;
  return Math.round((new Date(dateStr).getTime() - new Date().setHours(0, 0, 0, 0)) / 86_400_000);
};

/**
 * Egy emlékeztető adatai, szerkeszthetően.
 *
 * A naptárból és a listából is ugyanez nyílik meg. Alapból olvasó nézet: a
 * dolgozó a legtöbbször csak meg akarja nézni, mi az — a szerkesztés külön
 * lépés, hogy egy véletlen kattintás ne írjon át dátumot.
 */
export const ReminderDialog: React.FC<ReminderDialogProps> = ({
  reminder, shelterId, onClose, onChanged,
}) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [form, setForm] = useState({
    title: '', eventType: 'Vaccination' as MedicalEventType,
    nextDueDate: '', recurrenceIntervalDays: '', notes: '', veterinarian: '',
  });

  useEffect(() => {
    if (!reminder) return;
    setEditing(false);
    setConfirmingDelete(false);
    setForm({
      title: reminder.title || '',
      eventType: reminder.eventType,
      nextDueDate: reminder.nextDueDate || '',
      recurrenceIntervalDays: reminder.recurrenceIntervalDays?.toString() || '',
      notes: reminder.notes || '',
      veterinarian: reminder.veterinarian || '',
    });
  }, [reminder?.id]);

  useEffect(() => {
    if (!reminder) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [reminder, onClose]);

  if (!reminder) return null;

  const d = daysUntil(reminder.nextDueDate);
  const overdue = d !== null && d < 0;

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    const ok = await updateAnimalMedicalEvent(reminder.id, {
      title: form.title.trim(),
      eventType: form.eventType,
      nextDueDate: form.nextDueDate || null,
      recurrenceIntervalDays: form.recurrenceIntervalDays ? Number(form.recurrenceIntervalDays) : null,
      notes: form.notes.trim() || null,
      veterinarian: form.veterinarian.trim() || null,
    });
    setSaving(false);
    if (ok) { onChanged(); onClose(); }
  };

  const handleDelete = async () => {
    setSaving(true);
    const ok = await deleteAnimalMedicalEvent(reminder.id);
    setSaving(false);
    if (ok) { onChanged(); onClose(); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Emlékeztető részletei"
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 p-6 border-b border-gray-100">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Stethoscope className="w-4 h-4 text-teal-600 flex-shrink-0" />
              <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-gray-100 border border-gray-200 text-gray-600">
                {MEDICAL_EVENT_LABELS[reminder.eventType]}
              </span>
            </div>
            <h2 className="text-xl font-bold text-gray-900 truncate">{reminder.title}</h2>
            {shelterId && reminder.animal && (
              <Link
                to={`/shelter/${shelterId}/animals/${reminder.animal.id}/medical`}
                className="text-sm text-teal-700 hover:underline inline-flex items-center gap-1 mt-0.5"
              >
                {reminder.animal.name}
                <span className="text-gray-400">· {reminder.animal.species}</span>
                <ExternalLink className="w-3 h-3" />
              </Link>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors flex-shrink-0"
            aria-label="Bezárás"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto p-6 space-y-4">
          {!editing ? (
            <>
              <div className={`flex items-center gap-2 p-3 rounded-lg border ${overdue ? 'bg-red-50 border-red-200 text-red-800' : 'bg-gray-50 border-gray-200 text-gray-800'}`}>
                {overdue ? <AlertTriangle className="w-4 h-4" /> : <CalendarClock className="w-4 h-4" />}
                <span className="text-sm font-semibold">{reminder.nextDueDate || '—'}</span>
                {d !== null && (
                  <span className="text-xs text-gray-500">
                    ({overdue ? `${Math.abs(d)} napja lejárt` : d === 0 ? 'ma esedékes' : `${d} nap múlva`})
                  </span>
                )}
              </div>

              <dl className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className={labelCls}>Utolsó alkalom</dt>
                  <dd className="text-gray-800">{reminder.eventDate || '—'}</dd>
                </div>
                <div>
                  <dt className={labelCls}>Ismétlődés</dt>
                  <dd className="text-gray-800">
                    {reminder.recurrenceIntervalDays ? `${reminder.recurrenceIntervalDays} naponta` : 'Nincs megadva'}
                  </dd>
                </div>
                <div>
                  <dt className={labelCls}>Állatorvos</dt>
                  <dd className="text-gray-800">{reminder.veterinarian || '—'}</dd>
                </div>
              </dl>

              {reminder.notes && (
                <div>
                  <h3 className={labelCls}>Megjegyzés</h3>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{reminder.notes}</p>
                </div>
              )}
            </>
          ) : (
            <>
              <div>
                <label className={labelCls} htmlFor="rem-title">Megnevezés</label>
                <input id="rem-title" type="text" value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className={inputCls} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls} htmlFor="rem-type">Típus</label>
                  <select id="rem-type" value={form.eventType}
                    onChange={e => setForm(f => ({ ...f, eventType: e.target.value as MedicalEventType }))} className={inputCls}>
                    {MEDICAL_EVENT_TYPES.map(t => <option key={t} value={t}>{MEDICAL_EVENT_LABELS[t]}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls} htmlFor="rem-due">Esedékesség</label>
                  <input id="rem-due" type="date" value={form.nextDueDate}
                    onChange={e => setForm(f => ({ ...f, nextDueDate: e.target.value }))} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="rem-interval">Ismétlődés (nap)</label>
                  <input id="rem-interval" type="number" min="1" value={form.recurrenceIntervalDays}
                    onChange={e => setForm(f => ({ ...f, recurrenceIntervalDays: e.target.value }))} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="rem-vet">Állatorvos</label>
                  <input id="rem-vet" type="text" value={form.veterinarian}
                    onChange={e => setForm(f => ({ ...f, veterinarian: e.target.value }))} className={inputCls} />
                </div>
              </div>

              <div>
                <label className={labelCls} htmlFor="rem-notes">Megjegyzés</label>
                <textarea id="rem-notes" rows={3} value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className={inputCls} />
              </div>
            </>
          )}
        </div>

        <div className="p-6 border-t border-gray-100 flex items-center justify-between gap-3">
          {/* A törlés kétlépcsős: egy emlékeztető törlése nem visszavonható. */}
          {confirmingDelete ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-red-700 font-medium">Biztos törlöd?</span>
              <button type="button" onClick={handleDelete} disabled={saving}
                className="text-sm font-semibold px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-60">
                Igen, törlöm
              </button>
              <button type="button" onClick={() => setConfirmingDelete(false)}
                className="text-sm px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">
                Mégse
              </button>
            </div>
          ) : (
            <button type="button" onClick={() => setConfirmingDelete(true)} disabled={saving}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60">
              <Trash2 className="w-4 h-4" /> Törlés
            </button>
          )}

          <div className="flex items-center gap-2">
            {editing ? (
              <>
                <button type="button" onClick={() => setEditing(false)} disabled={saving}
                  className="text-sm px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-60">
                  Mégse
                </button>
                <button type="button" onClick={handleSave} disabled={saving || !form.title.trim()}
                  className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Mentés
                </button>
              </>
            ) : (
              <button type="button" onClick={() => setEditing(true)}
                className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700">
                <Pencil className="w-4 h-4" /> Szerkesztés
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
